import { useEffect, useMemo, useState } from "react";
import { Package, Plus, PencilSimple, Trash, X, ArrowRight, Link as LinkIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

const SESSION_TYPES = ["60 Minutes", "45 Minutes", "30 Minutes", "Group Session"];

export default function TrainerPackages() {
  const [packages, setPackages] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [applying, setApplying] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([api.get("/packages"), api.get("/clients")]);
      setPackages(p.data);
      setClients(c.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Archive this package? Existing payments referencing it will stay.")) return;
    try {
      await api.delete(`/packages/${id}`);
      toast.success("Package archived");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8" data-testid="trainer-packages">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="tracker text-brand mb-3">[ Services ]</div>
          <h1 className="font-display text-4xl sm:text-5xl">Packages.</h1>
          <p className="text-zinc-400 mt-3">Reusable bundles you can apply to any client to create a payment.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary" data-testid="open-package-form">
          <Plus size={16} weight="bold" /> New package
        </button>
      </div>

      {loading ? (
        <div className="card-flat p-16 text-center text-zinc-500 tracker">Loading…</div>
      ) : packages.length === 0 ? (
        <div className="card-flat p-16 text-center" data-testid="packages-empty">
          <Package size={32} weight="duotone" className="text-brand mx-auto mb-3" />
          <div className="font-display text-2xl mb-2">No packages yet.</div>
          <div className="text-zinc-500 tracker text-[10px] mb-4">Bundle multiple sessions into a flat-price offer.</div>
          <button onClick={() => setShowForm(true)} className="btn-primary mx-auto" data-testid="create-first-package">
            <Plus size={14} weight="bold" /> Create your first package
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="packages-grid">
          {packages.map((p) => (
            <PackageCard
              key={p.id}
              pkg={p}
              onEdit={() => { setEditing(p); setShowForm(true); }}
              onDelete={() => remove(p.id)}
              onApply={() => setApplying(p)}
            />
          ))}
        </div>
      )}

      {showForm && (
        <PackageForm
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onDone={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
      {applying && (
        <ApplyModal
          pkg={applying}
          clients={clients}
          onClose={() => setApplying(null)}
          onDone={() => { setApplying(null); load(); }}
        />
      )}
    </div>
  );
}

function PackageCard({ pkg, onEdit, onDelete, onApply }) {
  const publicUrl = pkg.slug ? `${window.location.origin}/p/${pkg.slug}` : null;
  const copyLink = async () => {
    if (!publicUrl) return toast.error("This package needs to be re-saved to get a public link");
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Public link copied — paste to Instagram, WhatsApp, anywhere");
    } catch (_e) {
      toast.error("Couldn't copy — long-press the URL on mobile");
    }
  };
  return (
    <div className="card-flat p-5 flex flex-col" data-testid={`package-card-${pkg.id}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl leading-tight truncate">{pkg.name}</div>
          <div className="tracker text-zinc-500 text-[10px] mt-1">{pkg.sessions_included} × {pkg.session_type}</div>
        </div>
        <div className="flex items-center gap-1">
          {publicUrl && (
            <button onClick={copyLink} className="btn-ghost !py-1.5 !px-2 !text-[10px]" title="Copy public link" data-testid={`share-package-${pkg.id}`}>
              <LinkIcon size={12} weight="bold" />
            </button>
          )}
          <button onClick={onEdit} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`edit-package-${pkg.id}`}>
            <PencilSimple size={12} weight="bold" />
          </button>
          <button onClick={onDelete} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`delete-package-${pkg.id}`}>
            <Trash size={12} weight="bold" />
          </button>
        </div>
      </div>
      {pkg.description && (
        <p className="text-sm text-zinc-400 mb-3 line-clamp-2">{pkg.description}</p>
      )}
      <div className="font-display text-3xl money mt-auto">{(pkg.currency || "usd").toUpperCase()} {Number(pkg.price).toFixed(2)}</div>
      <div className="tracker text-zinc-500 text-[10px] mt-2">
        Applied {pkg.applied_count || 0}× · by {pkg.trainer_name || "—"}
      </div>
      {publicUrl && (
        <a
          href={publicUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="tracker text-brand-light hover:underline text-[10px] mt-1 font-mono truncate"
          data-testid={`public-link-${pkg.id}`}
        >
          /p/{pkg.slug}
        </a>
      )}
      <button onClick={onApply} className="btn-primary justify-center mt-4" data-testid={`apply-package-${pkg.id}`}>
        Apply to client <ArrowRight size={14} weight="bold" />
      </button>
    </div>
  );
}

function PackageForm({ editing, onClose, onDone }) {
  const [form, setForm] = useState(() => ({
    name: editing?.name || "",
    description: editing?.description || "",
    price: editing?.price ?? 0,
    currency: editing?.currency || "usd",
    sessions_included: editing?.sessions_included || 4,
    session_type: editing?.session_type || "60 Minutes",
  }));
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        sessions_included: Number(form.sessions_included),
      };
      if (editing) {
        await api.patch(`/packages/${editing.id}`, payload);
        toast.success("Package updated");
      } else {
        await api.post("/packages", payload);
        toast.success("Package created");
      }
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-flat w-full max-w-lg p-8" onClick={(e) => e.stopPropagation()} data-testid="package-form-modal">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="tracker text-brand mb-2">[ {editing ? "Edit" : "New"} package ]</div>
            <h2 className="font-display text-3xl">{editing ? "Update." : "Bundle."}</h2>
          </div>
          <button onClick={onClose}><X size={20} weight="bold" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="tracker block mb-2">Name</label>
            <input required value={form.name} onChange={set("name")} className="w-full px-4 py-3 border" data-testid="package-name" placeholder="12-Session PT Bundle" />
          </div>
          <div>
            <label className="tracker block mb-2">Description</label>
            <textarea rows={2} value={form.description} onChange={set("description")} className="w-full px-4 py-3 border" data-testid="package-description" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="tracker block mb-2">Sessions included</label>
              <input required type="number" min="1" max="999" value={form.sessions_included} onChange={set("sessions_included")} className="w-full px-4 py-3 border" data-testid="package-sessions" />
            </div>
            <div>
              <label className="tracker block mb-2">Session type</label>
              <select value={form.session_type} onChange={set("session_type")} className="w-full px-4 py-3 border" data-testid="package-type">
                {SESSION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="tracker block mb-2">Total price</label>
              <input required type="number" min="0" step="0.01" value={form.price} onChange={set("price")} className="w-full px-4 py-3 border" data-testid="package-price" />
            </div>
            <div>
              <label className="tracker block mb-2">Currency</label>
              <select value={form.currency} onChange={set("currency")} className="w-full px-4 py-3 border" data-testid="package-currency">
                <option value="usd">USD</option><option value="eur">EUR</option><option value="gbp">GBP</option>
              </select>
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="package-submit">
            {loading ? "Saving…" : editing ? "Update package" : "Create package"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ApplyModal({ pkg, clients, onClose, onDone }) {
  const [clientId, setClientId] = useState(clients[0]?.id || "");
  const [loading, setLoading] = useState(false);
  const selectedClient = useMemo(() => clients.find((c) => c.id === clientId), [clients, clientId]);

  const apply = async (e) => {
    e.preventDefault();
    if (!clientId) return toast.error("Pick a client");
    setLoading(true);
    try {
      await api.post(`/packages/${pkg.id}/apply`, { client_id: clientId });
      toast.success(`Payment of ${pkgCurr} ${Number(pkg.price).toFixed(2)} created for ${selectedClient?.name}`);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const pkgCurr = (pkg.currency || "usd").toUpperCase();

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-flat w-full max-w-md p-8" onClick={(e) => e.stopPropagation()} data-testid="apply-package-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="tracker text-brand mb-2">[ Apply package ]</div>
            <h2 className="font-display text-2xl">{pkg.name}</h2>
            <div className="tracker text-zinc-500 text-[10px] mt-1">{pkg.sessions_included} × {pkg.session_type} · <span className="money">{pkgCurr} {Number(pkg.price).toFixed(2)}</span></div>
          </div>
          <button onClick={onClose}><X size={20} weight="bold" /></button>
        </div>
        <form onSubmit={apply} className="space-y-4">
          <div>
            <label className="tracker block mb-2">Client</label>
            <select required value={clientId} onChange={(e) => setClientId(e.target.value)} className="w-full px-4 py-3 border" data-testid="apply-client">
              {clients.length === 0 && <option value="">Add a client first</option>}
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name} — {pkgCurr} {Number(c.balance || 0).toFixed(2)} in pot</option>)}
            </select>
          </div>
          <div className="tracker text-zinc-500 text-[10px]">
            Creates a payment-due record for this client. When it&apos;s marked made (or paid through Stripe), the client&apos;s pot is topped up.
          </div>
          <button type="submit" disabled={loading || clients.length === 0} className="btn-primary w-full justify-center" data-testid="apply-submit">
            {loading ? "Applying…" : <>Create payment · <span className="money">{pkgCurr} {Number(pkg.price).toFixed(2)}</span></>}
          </button>
        </form>
      </div>
    </div>
  );
}
